import { useEffect, useState } from "react";
import { fnJson } from "@/lib/fn-fetch";

/** What hire-readiness.js returns for one person. */
export interface ReadinessItem { key: string; label: string; done: boolean; required: boolean }
export interface Readiness {
  state: "not_required" | "approved" | "ready_for_approval" | "in_progress";
  items: ReadinessItem[];
  missing: ReadinessItem[];
  done: number;
  total: number;
}

/** The checklist itself, shared by the new hire's card and the manager's page. */
export function ChecklistItems({ items }: { items: ReadinessItem[] }) {
  return (
    <ul className="space-y-1">
      {items.map(i => (
        <li key={i.key} className="flex items-start gap-2 text-sm">
          <span aria-hidden="true" className={i.done ? "text-ok" : "text-gray2"}>{i.done ? "✓" : "○"}</span>
          <span className={i.done ? "text-gray2" : ""}>
            {i.label}
            <span className="sr-only">{i.done ? " — done" : " — still to do"}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The new hire's own view of "what is left before I can be scheduled".
 *
 * HQ-HIRE-READY-001. Workflow map, Hire to active employee, step 4: "visible
 * completion status". Onboarding was a one-way form, and afterwards the new hire
 * had no way to see that the I-9, required training and equipment were still
 * outstanding. Renders nothing for anybody it does not apply to — everyone hired
 * before this existed, and everyone already approved — and nothing at all if the
 * lookup fails, because a broken checklist must never get between a person and
 * their dashboard.
 */
export function NewHireChecklist() {
  const [r, setR] = useState<Readiness | null>(null);

  useEffect(() => {
    let live = true;
    void fnJson<{ ok: boolean; readiness: Readiness }>("hire-readiness")
      .then(out => { if (live && out.ok) setR(out.readiness); })
      .catch(() => { /* not shown rather than shown broken */ });
    return () => { live = false; };
  }, []);

  if (!r || (r.state !== "in_progress" && r.state !== "ready_for_approval")) return null;

  const ready = r.state === "ready_for_approval";
  return (
    <section aria-labelledby="new-hire-checklist" className="card">
      <div className="flex items-center justify-between gap-3">
        <h2 id="new-hire-checklist" className="text-sm font-semibold">Getting you ready to start</h2>
        <span className="text-xs text-gray2">{r.done} of {r.total} done</span>
      </div>
      <div className="my-2 h-1.5 rounded-full bg-line" role="progressbar" aria-valuemin={0} aria-valuemax={r.total} aria-valuenow={r.done} aria-label="Getting ready progress">
        <div className="h-1.5 rounded-full bg-teal-dark" style={{ width: `${r.total ? Math.round((r.done / r.total) * 100) : 0}%` }} />
      </div>
      <ChecklistItems items={r.items} />
      <p className="mt-3 text-xs text-gray2">
        {ready
          ? "Everything is done. Your manager has been told and will approve you so you can be put on the schedule."
          : "Once everything is done, your manager approves you and you can be put on the schedule."}
      </p>
    </section>
  );
}
